/**
 * Data layer for `drill-down-analytics`. Thin typed fetch wrappers over /api/v1/drill-down-analytics.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface DrillDownAnalyticsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListDrillDownAnalyticsResponse {
	count: number;
	items: DrillDownAnalyticsRow[];
}

export interface CreateDrillDownAnalyticsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/drill-down-analytics";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListDrillDownAnalyticsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListDrillDownAnalyticsResponse>(res);
}

export async function create(body: CreateDrillDownAnalyticsBody): Promise<DrillDownAnalyticsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<DrillDownAnalyticsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const drillDownAnalyticsQueryKeys = {
	all: ["drill-down-analytics"] as const,
	list: () => [...drillDownAnalyticsQueryKeys.all] as const,
	detail: (id: string) => [...drillDownAnalyticsQueryKeys.all, id] as const,
};
